import { reactive, computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import type { Ref, ComputedRef } from 'vue'

const toDigits = (v: string | number) => {
  const str = typeof v === 'string' ? v : v.toString()

  let digits = ''
  let decimalAdded = false

  for (let i = 0; i < str.length; i += 1) {
    const c = str.charAt(i)

    if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(c)) {
      digits += c
    } else if (!decimalAdded && c === '.') {
      digits += c
      decimalAdded = true
    }
  }

  return digits
}

// format only the integer part
const delimitedDigits = (digits: string): string => {
  const [intPart, fracPart] = digits.split('.')
  const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return fracPart !== undefined ? `${intFmt}.${fracPart}` : intFmt
}

const countChar = (str: string, char: string) => {
  let count = 0

  for (let i = 0; i < str.length; i += 1) {
    if (str.charAt(i) === char) {
      count += 1
    }
  }

  return count
}

interface CursorState {
  selectionStart: number | null
  selectionEnd: number | null
  adjustment: number
  nextCursor: number
}

const inputKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
const inputKeysEdit = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Space', 'Tab', 'Delete']

/**
 *  Note: This composable is dependent on that the modelValue of the input element is already delimited.
 */

export const useCursorPositioning = (
  inputElement: Ref<HTMLInputElement | null | undefined> | ComputedRef<HTMLInputElement | undefined> | HTMLInputElement,
  allowDecimals = false,
) => {
  const cursorState = reactive<CursorState>({
    selectionStart: null,
    selectionEnd: null,
    adjustment: 0,
    nextCursor: -1,
  })

  const inputEl = computed(() => (inputElement instanceof HTMLInputElement ? inputElement : inputElement.value))

  /**
   * Set last delimited value on focus as the value can be adjusted outside of this composable (slider etc.)
   */
  const onFocus = (e: FocusEvent) => {
    const value = (e.target as HTMLInputElement).value

    if (value !== lastDelimitedValue.value) {
      lastDelimitedValue.value = value
    }
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (!inputEl.value) {
      return
    }

    cursorState.selectionStart = inputEl.value.selectionStart ?? null
    cursorState.selectionEnd = inputEl.value.selectionEnd ?? null

    if (inputKeys.includes(event.key) || (event.key === '.' && allowDecimals)) {
      cursorState.adjustment += 1
      return true
    }

    if (inputKeysEdit.includes(event.key)) {
      if (event.key === 'Backspace') {
        if (cursorState.selectionStart !== null && cursorState.selectionStart > 0) {
          cursorState.adjustment -= 1
        }
      }
      return true
    }

    if (event.metaKey || event.ctrlKey) {
      return true
    }

    event.preventDefault()

    return false
  }

  const lastDelimitedValue = ref('')

  const onInput = (e: Event) => {
    const newVal = (e.target as HTMLInputElement).value

    const digits = toDigits(newVal ?? '')

    if (digits === '') {
      cursorState.adjustment = 0
      return
    }

    if (cursorState.selectionStart === null) {
      return
    }

    const newDelimitedValue = delimitedDigits(digits)

    if (newDelimitedValue !== lastDelimitedValue.value) {
      const cursor = cursorState.selectionStart + cursorState.adjustment
      const spaceDiffBeforeCursor =
        countChar(newDelimitedValue.substring(0, cursor), ' ') -
        countChar(lastDelimitedValue.value.substring(0, cursor), ' ')

      cursorState.nextCursor = cursorState.selectionStart + cursorState.adjustment + spaceDiffBeforeCursor
      cursorState.adjustment = 0

      if (cursorState.nextCursor >= 0) {
        nextTick(() => {
          if (inputEl.value) {
            inputEl.value.setSelectionRange(cursorState.nextCursor, cursorState.nextCursor)
            cursorState.nextCursor = -1
          }
        })
      }
    } else {
      cursorState.adjustment = 0
    }

    lastDelimitedValue.value = delimitedDigits(toDigits(newVal ?? ''))
  }

  onMounted(() => {
    if (inputEl.value) {
      inputEl.value.addEventListener('keydown', onKeyDown)
      inputEl.value.addEventListener('input', onInput)
      inputEl.value.addEventListener('focus', onFocus)
    }
  })

  onUnmounted(() => {
    if (inputEl.value) {
      inputEl.value.removeEventListener('keydown', onKeyDown)
      inputEl.value.removeEventListener('input', onInput)
      inputEl.value.removeEventListener('focus', onFocus)
    }
  })
}
